import { Bin, SystemBin } from '../types/bin/bin';
import { BinStatus } from '../types/bin/binStatus';
import { Company } from '../types/company';
import { Container } from '../types/container';
import { Unilever } from '../types/customer/DHL/Unilever/Unilever';
import { Order } from '../types/order';
import { Product } from '../types/product';
import { ProductTransaction } from '../types/productTransaction';
import { HandlingUnit } from '../types/handling_unit/handlingUnit';
import { toGroupedMap } from './map.util';
import { FlowType } from '../types/order/orderFlow';
import { System } from '../types/system';
import { ProductMasterData } from '../types/product_master_data';

export const MAX_CONTAINER_DEPTH = 25;

export namespace ContainerUtil {
  export function getAvailableContainers(containers: Map<string, Container>, order: Order) {
    const inboundableContainers = [];
    const outboundableContainers = [];

    for (const container of containers.values()) {
      switch (order.type) {
        case FlowType.inbound:
          if (container.companyId !== order.companyId) continue;
          if (container.binId === SystemBin.id) inboundableContainers.push(container);
          break;
        case FlowType.move:
        case FlowType.outbound:
        case FlowType.disposal:
          if (container.stockLocationId !== order.fromStockLocationId) continue;
          if (container.binId !== SystemBin.id) outboundableContainers.push(container);
          break;
      }
    }

    return { inboundableContainers, outboundableContainers };
  }

  /**
   * Returns a list of all parent containers for a given container.
   * @param containers - Map<ContainerId, Container>
   * @param container - The container (or its id) to get all parents for.
   * @returns An array of all parent containers.
   */
  export function getAllParentContainers(
    containers: Map<string, Container>,
    container: Container | string,
  ): Container[] {
    if (typeof container === 'string') {
      const found = containers.get(container);
      if (!found) return [];
      container = found;
    }

    const parents = [];
    let depth = 0;
    const queue = [container];

    while (queue.length) {
      if (depth > MAX_CONTAINER_DEPTH) throw Error('Circular container dependency!');
      const current = queue.shift();

      if (current?.parentId) {
        const parent = containers.get(current?.parentId);
        if (parent) {
          depth++;
          parents.push(parent);
          queue.push(parent);
        }
      }
    }
    return parents;
  }

  /**
   * Returns a list of all child containers and products for a given container regardless of depth.
   * @param containers - Map<ContainerId, Container>
   * @param products - Map<ProductMasterDataId, Map<ProductId, Product>>
   * @param container - The container to get all children for.
   * @returns An array of all child containers and products.
   */
  export function getAllContainerChildren(
    containers: Map<string, Container>,
    products: Map<string, Map<string, Product>>,
    container: Container,
  ) {
    const childContainers = ContainerUtil.getAllChildContainers(containers, container);

    const childProducts = getProductsByContainerIds(
      products,
      [...childContainers, container].map(container => container.id),
    );

    return {
      childContainers,
      childProducts,
    };
  }
  /**
   * Returns a list of all child containers for a given container regardless of depth.
   * @param containers - Map<ContainerId, Container>
   * @param container - The container (or its id) to get all children for.
   * @returns An array of all child containers.
   */
  export function getAllChildContainers(
    containers: Map<string, Container>,
    container: Container | string,
  ): Container[] {
    if (typeof container === 'string') {
      const found = containers.get(container);
      if (!found) return [];
      container = found;
    }

    const containersByParentId = toGroupedMap([...containers.values()], 'parentId');

    const children = [];
    const queue = [{ container, depth: 0 }];

    while (queue.length) {
      const current = queue.shift();
      if (!current) break;
      if (current.depth > MAX_CONTAINER_DEPTH) throw Error('Circular container dependency!');

      const currentChildren = containersByParentId.get(current.container.id);
      if (!currentChildren?.length) continue;

      children.push(...currentChildren);
      queue.push(
        ...currentChildren.map(c => {
          return { container: c, depth: current.depth + 1 };
        }),
      );
    }

    return children;
  }

  /**
   * Returns a list of products that belong to the specified container IDs.
   * @param containers - Map<ContainerId, Container>
   * @param products - Map<ProductMasterDataId, Map<ProductId, Product>>
   * @returns An array of products that are direct descendants of the specified container IDs.
   */
  export function getProductsByContainerIds(
    products: Map<string, Map<string, Product>>,
    containerIds: string[],
  ): Product[] {
    const productsByContainerIds = toGroupedMap([...products.values()].map(m => [...m.values()]).flat(), 'containerId');
    const set = new Set(containerIds);

    return [...set.values()].map(id => productsByContainerIds.get(id) || []).flat();
  }
}

export function getOpenProductTransactionsByContainerId(
  productTransactions: Map<string, ProductTransaction>,
  containerId: string,
) {
  return [...productTransactions.values()].filter(
    pt => pt.statusId === System.ProductTransactionStatus.createdId && pt.product.containerId === containerId,
  );
}

export function getAllChildProductTransactions(
  productTransaction: ProductTransaction,
  productTransactions: Map<string, ProductTransaction>,
): ProductTransaction[] {
  const productTransactionsByContainerTransactionId = toGroupedMap(
    [...productTransactions.values()],
    'containerTransactionId',
  );

  const queue = [{ transaction: productTransaction, depth: 0 }];
  const children = [];

  while (queue.length) {
    const current = queue.shift();
    if (!current) break;
    if (current.depth > MAX_CONTAINER_DEPTH) {
      throw Error('Circular container dependency');
    }

    const currentChildren = productTransactionsByContainerTransactionId.get(current.transaction.id);
    if (!currentChildren?.length) continue;

    queue.push(
      ...currentChildren
        .filter(t => t.product.containerId)
        .map(t => {
          return { transaction: t, depth: current.depth + 1 };
        }),
    );
    children.push(...currentChildren);
  }

  return children;
}

interface ValidDestinationContainerProps {
  container: string | Container;

  stockLocationId: string;
  binId?: string;

  bins: Map<string, Bin>;
  binStatuses: Map<string, BinStatus>;

  containers: Map<string, Container>;

  company: Company;
}

export function isValidDestinationContainer({
  container,
  stockLocationId,
  binId,
  bins,
  binStatuses,
  containers,
  company,
}: ValidDestinationContainerProps) {
  if (typeof container === 'string') {
    const found = containers.get(container);
    if (!found) return false;
    container = found;
  }

  if (container.companyId !== company.id) return false;

  const containerBin = bins.get(container.binId);
  if (!containerBin && container.binId !== SystemBin.id) return false;

  if (container.binId !== SystemBin.id && container.stockLocationId !== stockLocationId) return false;

  const allowBinMismatch = company.settings.featureToggles.containers.allowBinMismatch;
  if (binId && container.binId !== binId && container.binId !== SystemBin.id && !allowBinMismatch) {
    return false;
  }

  if (containerBin) {
    const binStatus = binStatuses.get(containerBin.binStatusId);
    if (!binStatus?.inboundAllowed) return false;
  }

  return true;
}

export interface ValidSourceContainerProps {
  container: Container;
  transaction: ProductTransaction;
  products: Product[];
  productMasterData?: ProductMasterData;
}

export function isValidSourceContainer({
  transaction,
  container,
  products,
  productMasterData,
}: ValidSourceContainerProps) {
  if (Unilever.companyIds.includes(transaction.companyId)) {
    return Unilever.isValidSourceContainer({ transaction, container, products, productMasterData });
  }

  let items = [...products];

  if (transaction.product.fromStockLocationId) {
    items = items.filter(p => p.stockLocationId === transaction.product.fromStockLocationId);
  }

  if (transaction.product.fromBinId) items = items.filter(p => p.binId === transaction.product.fromBinId);
  if (transaction.product.lotId) items = items.filter(p => p.lotId === transaction.product.lotId);
  if (transaction.product.lpn) items = items.filter(p => p.lpn === transaction.product.lpn);
  if (transaction.product.serialNbr) items = items.filter(p => p.serial === transaction.product.serialNbr);

  return items.filter(p => p.companyId === transaction.companyId && p.containerId === container.id).length;
}

export function getRootContainerTransaction(
  transaction: ProductTransaction,
  productTransactions: Map<string, ProductTransaction>,
): ProductTransaction | undefined {
  if (!transaction.containerTransactionId) return undefined;

  const queue = [transaction];

  let depth = 0;
  while (queue.length) {
    if (depth > MAX_CONTAINER_DEPTH) throw Error('Circular container dependency');
    const current = queue.shift();
    if (!current || !current.containerTransactionId) return current;

    const parent = productTransactions.get(current.containerTransactionId);
    if (!parent) return current;
    depth++;

    queue.push(parent);
  }
}

export function determineProduct(
  transaction: ProductTransaction,
  products: Map<string, Map<string, Product>>,
  handlingUnits?: Map<string, HandlingUnit>,
): Product | undefined {
  const items = [...products.values()].map(ps => [...ps.values()]).flat();

  const hashes = new Set<string>();
  const selectedHandlingUnit = handlingUnits?.get(transaction.product.handlingUnitId || '');
  if (selectedHandlingUnit && selectedHandlingUnit.parentId !== transaction.product.pmdId) {
    const parentTransaction = new ProductTransaction(transaction);
    parentTransaction.product.pmdId = selectedHandlingUnit.parentId;
    hashes.add(Product.hash(Product.forOutbound(parentTransaction)));
  } else {
    hashes.add(Product.hash(Product.forOutbound(transaction)));
  }

  const foundItems = items.filter(p => {
    return hashes.has(Product.hash(p));
  });

  if (!foundItems.length || foundItems.length > 1) return undefined;

  return foundItems[0];
}
